/**
 * Helper utilities for implementing optimistic locking across resources.
 *
 * These utilities provide common patterns and helpers to make it easier
 * to add optimistic locking to any resource.
 */

import { AxiosResponse } from "axios";

/**
 * Helper type for resources that support optimistic locking
 */
export interface VersionedResource {
  id: number | string;
  version: number;
}

/**
 * Configuration for creating an optimistic locking API wrapper
 */
export interface OptimisticLockingApiConfig<T, TUpdateData = any> {
  /** API read method */
  read: (id: string | number, token?: string) => Promise<AxiosResponse<T>>;

  /** API update method */
  update: (params: {
    id: string;
    data: TUpdateData;
    token?: string;
    headers?: Record<string, string>;
  }) => Promise<AxiosResponse<T>>;
}

/**
 * Creates an optimistic locking aware API wrapper
 *
 * @example
 * ```typescript
 * const sourceApi = createOptimisticLockingApi({
 *   read: (id, token) => sourcesApi.read({ id, token }),
 *   update: (params) => sourcesApi.update(params),
 * });
 *
 * // Use in hook
 * const lockingHook = useOptimisticLocking({
 *   apiRead: (id) => sourceApi.read(id, token),
 *   apiUpdate: (id, data) => sourceApi.update(id, data, token),
 *   // ...
 * });
 * ```
 */
export function createOptimisticLockingApi<
  T extends VersionedResource,
  TUpdateData = any,
>(config: OptimisticLockingApiConfig<T, TUpdateData>) {
  return {
    read: (id: string | number, token?: string) => config.read(id, token),

    update: (
      id: string | number,
      data: TUpdateData,
      token?: string,
      headers?: Record<string, string>,
    ) =>
      config.update({
        id: id.toString() + "/",
        data,
        token,
        headers,
      }),
  };
}

/**
 * Helper to prepare update data with version included
 *
 * @example
 * ```typescript
 * const updateData = withVersion(formData, currentVersion);
 * // Result: { ...formData, version: currentVersion }
 * ```
 */
export function withVersion<T extends Record<string, any>>(
  data: T,
  version: number,
): T & { version: number } {
  return {
    ...data,
    version,
  };
}

/**
 * Helper to create a version-aware form data object
 */
export function createVersionedFormData<T extends Record<string, any>>(
  data: T,
  currentVersion: number,
  transformers?: {
    [K in keyof T]?: (value: T[K]) => any;
  },
): T & { version: number } {
  const transformed: any = { ...data };

  if (transformers) {
    Object.entries(transformers).forEach(([key, transformer]) => {
      if (transformer && key in data) {
        transformed[key] = transformer(data[key as keyof T]);
      }
    });
  }

  return withVersion(transformed, currentVersion);
}

/**
 * Common date transformer for form data
 */
export const dateTransformers = {
  toApiFormat: (date: Date | null | undefined): string | null => {
    return date?.toLocaleDateString("sv") || null;
  },

  fromApiFormat: (dateString: string | null): Date | null => {
    return dateString ? new Date(dateString) : null;
  },
};

/**
 * Helper to extract conflict information from error response
 */
export function extractConflictInfo(error: any): {
  isConflict: boolean;
  currentVersion?: number;
  message?: string;
} {
  if (error?.response?.data?.conflict) {
    return {
      isConflict: true,
      currentVersion: error.response.data.current_version,
      message: error.response.data.version,
    };
  }

  return { isConflict: false };
}

/**
 * Type guard to check if a resource has version field
 */
export function hasVersion(resource: any): resource is VersionedResource {
  return (
    resource !== null &&
    typeof resource === "object" &&
    "version" in resource &&
    typeof resource.version === "number"
  );
}

/**
 * Creates a toast configuration for optimistic locking events using i18n
 * @param resourceName - The name of the resource (localized)
 * @param t - Translation function from useTranslation
 */
export function createOptimisticLockingToasts(resourceName: string, t?: any) {
  // If t function is provided, use localized strings
  if (t) {
    return {
      updateSuccess: {
        title: t("common.toast.updated_successfully", {
          resource: resourceName,
        }),
      },

      conflictDetected: {
        title: t("common.toast.conflict_detected", { resource: resourceName }),
        description: t("common.toast.conflict_description", {
          resource: resourceName.toLowerCase(),
        }),
      },

      refreshSuccess: {
        title: t("common.toast.refreshed", { resource: resourceName }),
        description: t("common.toast.refreshed_description", {
          resource: resourceName.toLowerCase(),
        }),
      },

      overwriteSuccess: {
        title: t("common.toast.overwritten", { resource: resourceName }),
        description: t("common.toast.overwritten_description"),
      },

      error: (message: string) => ({
        title: t("common.toast.error", { item: message }),
        variant: "destructive" as const,
      }),
    };
  }

  // Fallback for backward compatibility (without t function)
  return {
    updateSuccess: {
      title: t("common.toast.updated_successfully", { resource: resourceName }),
    },

    conflictDetected: {
      title: t("common.toast.conflict_detected", { resource: resourceName }),
      description: t("common.toast.conflict_description", {
        resource: resourceName.toLowerCase(),
      }),
    },

    refreshSuccess: {
      title: t("common.toast.refreshed", { resource: resourceName }),
      description: t("common.toast.refreshed_description", {
        resource: resourceName.toLowerCase(),
      }),
    },

    overwriteSuccess: {
      title: t("common.toast.overwritten", { resource: resourceName }),
      description: t("common.toast.overwritten_description"),
    },

    error: (message: string) => ({
      title: t("common.toast.error", { item: message }),
      variant: "destructive" as const,
    }),
  };
}
